import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  Download, ArrowRight, ArrowLeft, GraduationCap, MessageSquareQuote,
  Mail, Loader2, CheckCircle2, ListChecks, Languages, Printer,
} from 'lucide-react';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { LegalModal } from '../components/LegalModal';
import { faqData, legalNoticeData, cookiePolicyData } from '../data/legalContent';
import Seo from '../components/Seo';
import { trackResourceLead } from '../lib/analytics';
import { VOCAB_HUB, getTopic, topicPath } from '../config/vocabulario';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE = 'https://spanischenlared.com';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// *palabra* en los datos significa cursiva, nunca un asterisco visible.
const richText = (text) =>
  text.split(/\*(.+?)\*/g).map((chunk, i) =>
    i % 2 ? <em key={i} className="font-semibold text-[#4A4A4A]">{chunk}</em> : chunk
  );

const LEVEL_BADGE = {
  A1: 'bg-green-100 text-green-700 border-green-300',
  A2: 'bg-blue-100 text-blue-700 border-blue-300',
};

const LEVEL_PAGE = { A1: '/espanol-a1', A2: '/espanol-a2' };

export default function VocabularioTemaPage() {
  const { slug } = useParams();
  const topic = getTopic(slug);
  const [email, setEmail] = useState('');
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [modalType, setModalType] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    setDone(false);
    setError('');
  }, [slug]);

  if (!topic) return <Navigate to={VOCAB_HUB.path} replace />;

  const url = `${SITE}${topicPath(topic.slug)}`;
  const related = (topic.related || []).map(getTopic).filter(Boolean);

  const handleDownload = async (e) => {
    e.preventDefault();
    const clean = email.trim().toLowerCase();
    if (!EMAIL_RE.test(clean)) {
      setError('Introduce un email válido.');
      return;
    }
    if (!privacy) {
      setError('Debes aceptar la Política de Privacidad para descargar la ficha.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/vocabulario/${topic.slug}/pdf`,
        { email: clean, marketing_consent: marketing },
        { responseType: 'blob' }
      );
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `vocabulario-${topic.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
      trackResourceLead(`vocabulario_${topic.slug.replace('-espanol-', '-')}`);
      setDone(true);
    } catch (err) {
      setError('Hubo un error al preparar la descarga. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Recursos', item: `${SITE}/#resources` },
          { '@type': 'ListItem', position: 3, name: 'Vocabulario', item: `${SITE}${VOCAB_HUB.path}` },
          { '@type': 'ListItem', position: 4, name: topic.title, item: url },
        ],
      },
      {
        '@type': 'LearningResource',
        name: topic.h1,
        description: topic.seoDescription,
        url,
        inLanguage: 'es',
        learningResourceType: 'Vocabulary list',
        educationalLevel: topic.level,
        teaches: topic.title,
        isAccessibleForFree: true,
        provider: { '@type': 'Organization', name: 'Spanisch en la Red', url: SITE },
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white text-[#4A4A4A]">
      <Seo path={topicPath(topic.slug)} fullTitle={topic.seoTitle} description={topic.seoDescription} type="article" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <Navigation />

      <section className="pt-32 pb-10 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <nav aria-label="Breadcrumb" className="text-xs text-gray-500 mb-6" data-testid="vocab-breadcrumb">
            <Link to="/" className="hover:text-[#7DBDD9]">Inicio</Link>
            <span className="mx-2">/</span>
            <Link to="/#resources" className="hover:text-[#7DBDD9]">Recursos</Link>
            <span className="mx-2">/</span>
            <Link to={VOCAB_HUB.path} className="hover:text-[#7DBDD9]">Vocabulario</Link>
            <span className="mx-2">/</span>
            <span className="text-[#4A4A4A] font-semibold">{topic.title}</span>
          </nav>

          <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold border ${LEVEL_BADGE[topic.level]}`}>
            Nivel {topic.level}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-[#4A4A4A] mt-4 mb-6 leading-tight" data-testid="vocab-topic-h1">
            {topic.h1}
          </h1>

          <div className="space-y-4" data-testid="vocab-intro">
            {topic.intro.split('\n\n').map((p, i) => (
              <p key={i} className="text-base text-gray-600 leading-relaxed">
                {richText(p)}
              </p>
            ))}
          </div>

          <a
            href="#descargar"
            data-testid="vocab-pdf-anchor"
            className="mt-7 inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur border border-[#A8D8EA] text-[#4A4A4A] rounded-full font-semibold text-sm shadow-sm hover:shadow-md transition-all"
          >
            <Download className="w-4 h-4 text-[#7DBDD9]" />
            Descargar esta ficha en PDF
          </a>
        </div>
      </section>

      {/* Qué incluye el recurso (la tabla completa va solo dentro del PDF) */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] uppercase tracking-wider mb-2">
            Qué incluye esta ficha de vocabulario
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-8" />

          <p className="text-base text-gray-600 leading-relaxed mb-8" data-testid="vocab-resource-summary">
            La ficha de <strong className="text-[#4A4A4A]">{topic.title.toLowerCase()}</strong> reúne{' '}
            <strong className="text-[#4A4A4A]">{topic.termCount} palabras y expresiones</strong> de nivel{' '}
            {topic.level}, cada una con su traducción al inglés y al alemán, además de frases de ejemplo para
            verlas en contexto. Es un PDF listo para imprimir o estudiar en el móvil, con el vocabulario
            agrupado para que puedas repasarlo de un vistazo antes de usarlo en una conversación.
          </p>

          <ul className="grid sm:grid-cols-2 gap-4" data-testid="vocab-resource-includes">
            {[
              { icon: ListChecks, t: `${topic.termCount} palabras esenciales`, d: `El vocabulario de ${topic.title.toLowerCase()} que de verdad se usa en el nivel ${topic.level}.` },
              { icon: Languages, t: 'Español, inglés y alemán', d: 'Cada palabra con su traducción, para entenderla sin diccionario.' },
              { icon: MessageSquareQuote, t: 'Frases de ejemplo', d: 'Ejemplos en contexto para aprender a usar las palabras, no solo a reconocerlas.' },
              { icon: Printer, t: 'PDF listo para imprimir', d: 'Pensado para repasar y practicar en papel o en pantalla.' },
            ].map(({ icon: Icon, t, d }) => (
              <li key={t} className="flex items-start gap-3 bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                <span className="w-9 h-9 rounded-xl bg-[#E8F5FA] flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#7DBDD9]" />
                </span>
                <span>
                  <strong className="block text-sm font-bold text-[#4A4A4A] mb-1">{t}</strong>
                  <span className="text-sm text-gray-600 leading-relaxed">{d}</span>
                </span>
              </li>
            ))}
          </ul>

          <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] uppercase tracking-wider mt-12 mb-2">
            Frases de ejemplo
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-6" />
          <ul className="space-y-3" data-testid="vocab-examples">
            {topic.examples.map((ex) => (
              <li key={ex} className="flex items-start gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <MessageSquareQuote className="w-4 h-4 text-[#7DBDD9] flex-shrink-0 mt-1" />
                <span className="text-sm text-[#4A4A4A] italic">{richText(ex)}</span>
              </li>
            ))}
          </ul>

          {/* Descarga del PDF con captura de email */}
          <div
            id="descargar"
            className="mt-14 rounded-3xl overflow-hidden shadow-2xl border border-gray-100 scroll-mt-28"
          >
            <div className="bg-gradient-to-r from-[#E8F5FA] to-[#FFF0EF] p-6">
              <h2 className="text-base md:text-lg font-bold text-[#4A4A4A]">
                Descarga gratis la ficha de {topic.title.toLowerCase()} en PDF
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Todo el vocabulario con inglés y alemán, las frases de ejemplo y un diseño pensado para
                imprimir y repasar donde quieras.
              </p>
            </div>

            {done ? (
              <div className="p-8 text-center bg-white" data-testid="vocab-download-success">
                <CheckCircle2 className="w-12 h-12 text-[#7DBDD9] mx-auto mb-4" />
                <p className="text-base font-bold text-[#4A4A4A] mb-2">¡Listo! Tu ficha ya se está descargando.</p>
                <p className="text-sm text-gray-600">
                  Si la descarga no ha empezado automáticamente, vuelve a pulsar el botón.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="vocab-download-again"
                  className="mt-6 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold text-sm shadow-md hover:shadow-lg transition-all"
                >
                  <Download className="w-4 h-4" />
                  Descargar de nuevo
                </button>
              </div>
            ) : (
              <form onSubmit={handleDownload} className="p-6 space-y-4 bg-white" data-testid="vocab-download-form">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@email.com"
                    aria-label="Correo electrónico"
                    inputMode="email"
                    autoComplete="email"
                    required
                    data-testid="vocab-email-input"
                    className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none"
                  />
                </div>

                <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={privacy}
                    onChange={(e) => setPrivacy(e.target.checked)}
                    data-testid="vocab-privacy-checkbox"
                    className="mt-0.5 w-4 h-4 accent-[#7DBDD9] flex-shrink-0"
                  />
                  <span>
                    He leído y acepto la{' '}
                    <button
                      type="button"
                      onClick={() => setModalType('cookies')}
                      data-testid="vocab-privacy-link"
                      className="text-[#7DBDD9] font-semibold underline hover:text-[#5DA8CC]"
                    >
                      Política de Privacidad
                    </button>
                    .
                  </span>
                </label>

                <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketing}
                    onChange={(e) => setMarketing(e.target.checked)}
                    data-testid="vocab-marketing-checkbox"
                    className="mt-0.5 w-4 h-4 accent-[#7DBDD9] flex-shrink-0"
                  />
                  <span>
                    Quiero recibir por email nuevas fichas de vocabulario, recursos gratuitos y consejos
                    para aprender español.
                  </span>
                </label>

                {error && (
                  <p className="text-sm text-red-500 font-semibold" data-testid="vocab-download-error">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="vocab-pdf-btn"
                  className="w-full py-3.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
                  {loading ? 'Preparando tu ficha...' : 'Descargar PDF gratis'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Relacionados + enlaces internos */}
      <section className="py-12 bg-gradient-to-b from-[#E8F5FA] to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] uppercase tracking-wider mb-2">
            También te puede interesar
          </h2>
          <div className="w-16 h-1 bg-gradient-to-r from-[#A8D8EA] to-[#F5A6A4] mb-8" />
          <div className="grid md:grid-cols-3 gap-5" data-testid="vocab-related">
            {related.map((r) => (
              <motion.div key={r.slug} whileHover={{ y: -5 }} className="bg-white rounded-2xl p-5 shadow-lg border border-gray-100">
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${LEVEL_BADGE[r.level]}`}>
                  {r.level}
                </span>
                <h3 className="text-base font-bold text-[#4A4A4A] mt-3 mb-2">{r.title}</h3>
                <p className="text-xs text-gray-600 mb-4">{r.short}</p>
                <Link
                  to={topicPath(r.slug)}
                  data-testid={`vocab-related-${r.slug}`}
                  className="text-sm font-semibold text-[#7DBDD9] hover:text-[#5DA8CC] inline-flex items-center gap-1"
                >
                  Ver vocabulario <ArrowRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to={VOCAB_HUB.path}
              data-testid="vocab-back-hub"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#7DBDD9] hover:text-[#5DA8CC]"
            >
              <ArrowLeft className="w-4 h-4" />
              Todo el vocabulario por temas
            </Link>
            <Link
              to={LEVEL_PAGE[topic.level]}
              data-testid="vocab-level-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#E38987] hover:text-[#D37775]"
            >
              Clases de español {topic.level}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-gradient-to-r from-[#E8F5FA] to-[#FFF0EF] rounded-3xl p-8 shadow-lg">
            <GraduationCap className="w-10 h-10 text-[#7DBDD9] mx-auto mb-4" />
            <h2 className="text-base md:text-lg font-bold text-[#4A4A4A] mb-2">
              ¿No sabes cuál es tu nivel de español?
            </h2>
            <p className="text-sm text-gray-600 mb-6">Haz el test de nivel gratuito y descúbrelo en 5 minutos.</p>
            <Link
              to="/test-de-nivel"
              data-testid="vocab-test-cta"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-bold shadow-lg hover:shadow-xl transition-all"
            >
              Hacer test de nivel
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <LegalModal
        isOpen={modalType !== null}
        onClose={() => setModalType(null)}
        type={modalType}
        faqData={faqData}
        legalNoticeData={legalNoticeData}
        cookiePolicyData={cookiePolicyData}
      />
    </div>
  );
}
